/**
 * Enum values shared by the Prisma schema, API validation, and frontend forms.
 *
 * These are duplicated in `apps/api/prisma/schema.prisma` because Prisma cannot
 * import them. If you change one, change both — `enums.test.ts` does not and
 * cannot catch the drift, but the RLS suite will fail on an unknown enum value
 * the first time a policy comparison misses.
 */
export const ACTOR_TYPES = ['STAFF', 'OWNER', 'TENANT', 'APPLICANT', 'PUBLIC', 'VENDOR'];
export const STAFF_ROLES = [
    'ADMIN',
    'PROPERTY_MANAGER',
    'MAINTENANCE',
    'ACCOUNTING',
    'DEVELOPER',
];
/** UI preference only — never read by RLS policies or business logic. */
export const THEMES = ['LIGHT', 'DARK'];
export const PROPERTY_TYPES = [
    'SINGLE_FAMILY',
    'MULTI_FAMILY',
    'CONDO',
    'TOWNHOUSE',
    'COMMERCIAL',
];
export const UNIT_STATUSES = ['AVAILABLE', 'OCCUPIED', 'TURNOVER', 'OFFLINE'];
export const LEASE_STATUSES = ['DRAFT', 'ACTIVE', 'MONTH_TO_MONTH', 'ENDED', 'TERMINATED'];
export const CHARGE_TYPES = [
    'RENT',
    'SECURITY_DEPOSIT',
    /** The day-5 late fee — see FeeSchedule.lateFee5DayAmount. */
    'LATE_FEE',
    /** An additional fee once rent is still unpaid at day 10 — stacks with LATE_FEE. */
    'LATE_FEE_10_DAY',
    /** Fires once on a lease's exact 6th consecutive late month. */
    'HABITUAL_LATE_FEE',
    'NSF_FEE',
    'UTILITY',
    'CONCESSION',
    'DAMAGE',
    /** A balance carried in from before a lease existed in this system. */
    'OPENING_BALANCE',
    'OTHER',
];
export const PAYMENT_METHODS = ['ACH', 'CHECK', 'CASH', 'MONEY_ORDER', 'CARD', 'OTHER'];
/**
 * ACH settles in roughly 4 business days. SETTLED is the only status that may
 * start the `FUNDS_HOLD_DAYS` clock toward owner disbursement — see invariant 4.
 * RETURNED can arrive long after SETTLED and is never a terminal-safe state.
 */
export const PAYMENT_STATUSES = [
    'PENDING',
    'PROCESSING',
    'SETTLED',
    'RETURNED',
    'FAILED',
    'CANCELED',
];
export const PAYMENT_EVENT_TYPES = [
    'CREATED',
    'SUBMITTED',
    'PROCESSING',
    'SETTLED',
    'RETURNED',
    'FAILED',
    'CANCELED',
    'ALLOCATED',
    'ALLOCATION_REVERSED',
    'REFUNDED',
    'NOTE',
];
export const DOCUMENT_TYPES = [
    'LEASE',
    'LEASE_ADDENDUM',
    'MOVE_IN_CHECKLIST',
    'MOVE_OUT_CHECKLIST',
    'DEPOSIT_NOTICE',
    'DAMAGES_NOTICE',
    'NOTICE',
    'INSPECTION_PHOTO',
    'PROPERTY_PHOTO',
    'UNIT_PHOTO',
    'TENANT_PHOTO',
    'OWNER_PHOTO',
    'INVOICE',
    'OTHER',
];
/** Who a document may be released to. Staff always see everything. */
export const DOCUMENT_VISIBILITIES = ['STAFF_ONLY', 'TENANT', 'OWNER', 'TENANT_AND_OWNER'];
export const CHECKLIST_KINDS = ['MOVE_IN', 'MOVE_OUT'];
export const CHECKLIST_CONDITIONS = ['GOOD', 'FAIR', 'POOR', 'DAMAGED', 'MISSING'];
/**
 * DEPOSIT_NOTICE and DAMAGES_NOTICE are generated automatically (MCL 554.603 /
 * 554.609 — see michigan.ts). The rest are staff-created with an explicit due
 * date; there's no statutory formula for a lease renewal or rent increase.
 */
export const NOTICE_KINDS = [
    'DEPOSIT_NOTICE',
    'DAMAGES_NOTICE',
    'MOVE_IN_CHECKLIST',
    'LEASE_RENEWAL',
    'RENT_INCREASE',
    'OTHER',
];
/**
 * Kept as a separate enum from NoticeKind even though six of its eight
 * values are the same strings — see the matching doc comment on the
 * DocumentTemplateKind Prisma enum in schema.prisma for why (NoticeKind is
 * structurally tied to the Notice model's compliance-clock fields;
 * LEASE_AGREEMENT and DECLINE_NOTICE never go through a Notice row at all).
 */
export const DOCUMENT_TEMPLATE_KINDS = [
    'DEPOSIT_NOTICE',
    'DAMAGES_NOTICE',
    'MOVE_IN_CHECKLIST',
    'LEASE_RENEWAL',
    'RENT_INCREASE',
    'OTHER',
    'DECLINE_NOTICE',
    'LEASE_AGREEMENT',
    'OWNER_STATEMENT',
    'PORTAL_INVITE_EMAIL',
    'PASSWORD_RESET_EMAIL',
];
/**
 * The two DocumentTemplateKind values that are transactional emails (portal
 * invite, password reset) rather than a notice/lease PDF — single source of
 * truth for both the API's preview/send-test-email branching
 * (modules/leases/routes.ts) and the composer UI's per-kind field/token-group
 * selection (DocumentTemplateList.tsx).
 */
export const TRANSACTIONAL_EMAIL_TEMPLATE_KINDS = [
    'PORTAL_INVITE_EMAIL',
    'PASSWORD_RESET_EMAIL',
];
/**
 * The four public compliance/policy pages (/compliance, /privacy-policy,
 * /terms-of-use, /equal-housing) — see the CompliancePage Prisma model.
 */
export const COMPLIANCE_PAGE_KINDS = [
    'COMPLIANCE',
    'PRIVACY_POLICY',
    'TERMS_OF_USE',
    'EQUAL_HOUSING',
];
/**
 * TRANSFERRED/PAID/PAYOUT_FAILED are reachable only through the deferred
 * Stripe Connect adapter — this pass's service layer never sets them.
 * SETTLED_EXTERNALLY is the only reachable "money moved" state today. See
 * the matching doc comment on the Prisma enum in schema.prisma.
 */
export const OWNER_DISBURSEMENT_STATUSES = [
    'APPROVED',
    'TRANSFERRED',
    'PAID',
    'PAYOUT_FAILED',
    'SETTLED_EXTERNALLY',
];
export const OWNER_STATEMENT_LINE_TYPES = [
    'PROPERTY_INCOME',
    'FEE_PAYMENT',
    'FEE_MANAGEMENT',
    'FEE_ACCOUNT_MONTHLY',
    'ADJUSTMENT_CLAWBACK',
    'ADJUSTMENT_OTHER',
];
export const AUDIT_ACTIONS = [
    'CREATE',
    'UPDATE',
    'DELETE',
    'READ',
    'LOGIN',
    'LOGOUT',
    'CHANGE_PASSWORD',
];
/**
 * Stripe Connect owner self-enrollment. Driven by the account.updated (v2
 * thin event) webhook path, except NOT_INVITED -> INVITED (staff sends an
 * invite) and INVITED -> ONBOARDING_STARTED (owner mints their first Account
 * Link). REQUIREMENTS_DUE is the state any ENROLLED owner can fall back
 * into — an owner in it cannot receive a Transfer. See the matching Prisma
 * enum's doc comment in schema.prisma and CLAUDE.md's Owner disbursements
 * section.
 */
export const OWNER_CONNECT_STATUSES = [
    'NOT_INVITED',
    'INVITED',
    'ONBOARDING_STARTED',
    'PENDING_VERIFICATION',
    'ENROLLED',
    'REQUIREMENTS_DUE',
];
/**
 * Vendor payouts — step 14. Mirrors OWNER_DISBURSEMENT_STATUSES exactly,
 * except there is no equivalent of a fee line (the vendor receives the full
 * WorkOrder.cost) and no period — see VendorDisbursement's own doc comment
 * in schema.prisma for the idempotency mechanism that replaces
 * (ownerId, periodStart).
 */
export const VENDOR_DISBURSEMENT_STATUSES = [
    'APPROVED',
    'TRANSFERRED',
    'PAID',
    'PAYOUT_FAILED',
    'SETTLED_EXTERNALLY',
];
/**
 * Stripe Connect vendor self-enrollment — same shape as
 * OWNER_CONNECT_STATUSES, driven by the same account.updated (v2 thin
 * event) webhook path. See CLAUDE.md's "Vendor disbursements" section.
 */
export const VENDOR_CONNECT_STATUSES = [
    'NOT_INVITED',
    'INVITED',
    'ONBOARDING_STARTED',
    'PENDING_VERIFICATION',
    'ENROLLED',
    'REQUIREMENTS_DUE',
];
export const WORK_ORDER_STATUSES = [
    'OPEN',
    'ASSIGNED',
    'IN_PROGRESS',
    'ON_HOLD',
    'COMPLETED',
    'CANCELED',
];
export const WORK_ORDER_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'EMERGENCY'];
/**
 * DRAFT is writable by the owning applicant (invariant 8); everything past it
 * is staff-only per the RLS policy split in rls-policies.sql. No separate
 * "under review" state — the staff queue is just SUBMITTED, oldest first.
 */
export const APPLICATION_STATUSES = [
    'DRAFT',
    'SUBMITTED',
    'APPROVED',
    'DECLINED',
    'WITHDRAWN',
];
export const SCREENING_DECISION_KINDS = ['APPROVED', 'DECLINED'];
/**
 * TCPA-style SMS consent. NOT_ASKED is the only state that blocks the
 * messaging module's Text button — see resolveConversationTarget
 * (modules/messaging/service.ts) and the matching Prisma enum's doc comment
 * in schema.prisma.
 */
export const SMS_CONSENT_STATUSES = ['NOT_ASKED', 'CONSENTED', 'DECLINED'];
/**
 * Whether an SMS consent decision was recorded by staff, mirrored off a
 * Surge contact.opted_in/contact.opted_out webhook (the tenant replying
 * START/STOP directly), or recorded by the tenant themselves from the
 * portal popup — alongside, not instead of, the staff-recorded path.
 */
export const SMS_CONSENT_SOURCES = ['STAFF', 'SURGE_WEBHOOK', 'TENANT_PORTAL'];
//# sourceMappingURL=enums.js.map